import {
  connectGradeOrder,
  type GradeKey,
  sortGradesByOrder,
} from "@nimble/datamodel/enums/connectGrades";
import type { CoreGradeNumber } from "@nimble/datamodel/enums/coreGrades";

export function getGradeLabel(grade: GradeKey): string {
  // Use simplified label for numeric grades in ranges (1-5 instead of 1st-5th)
  if (grade === "PK") return "PK";
  if (grade === "KG") return "K";
  return grade; // Just return "1", "2", etc.
}

const NUMERIC_GRADE_LABELS: Record<CoreGradeNumber, string> = {
  0: "PK",
  13: "K",
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
  8: "8th",
  9: "9th",
  10: "10th",
  11: "11th",
  12: "12th",
};

export function getGradeLabelWithSuffix(grade: CoreGradeNumber): string {
  return NUMERIC_GRADE_LABELS[grade];
}

function gradeSortKey(grade: CoreGradeNumber): number {
  if (grade === 0) return -2;
  if (grade === 13) return -1;
  return grade;
}

export function sortGradeNumbers(
  grades: readonly CoreGradeNumber[],
): CoreGradeNumber[] {
  return [...grades].sort((a, b) => gradeSortKey(a) - gradeSortKey(b));
}

export function formatGradeRange(grades: readonly string[]): string {
  const sorted = sortGradesByOrder(grades);
  if (sorted.length === 0) return "";

  const firstGrade = sorted.at(0);
  const lastGrade = sorted.at(-1);

  if (!firstGrade || !lastGrade) return "";

  if (firstGrade === lastGrade) {
    return getGradeLabel(firstGrade);
  }

  return `${getGradeLabel(firstGrade)}-${getGradeLabel(lastGrade)}`;
}

export function formatGradesRangeOrList(grades: readonly string[]): string {
  const sorted = sortGradesByOrder(grades);

  const unique: GradeKey[] = [];
  for (const g of sorted) {
    if (!unique.includes(g)) unique.push(g);
  }

  if (unique.length === 0) return "";
  if (unique.length === 1) return getGradeLabel(unique[0] as GradeKey);

  const indices = unique.map((g) => connectGradeOrder.indexOf(g));
  const isContiguous = indices.every((idx, i) => {
    if (i === 0) return true;
    const previousIndex = indices[i - 1];
    return previousIndex !== undefined && idx === previousIndex + 1;
  });

  return isContiguous
    ? formatGradeRange(unique)
    : unique.map(getGradeLabel).join(", ");
}
